import { addToast } from ":shared/lib/toast";
import {
	QueryClient,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import { database } from ":shared/database/connection";
import { generateDatabaseProperties } from ":shared/database/get-table";
import { createTag, type Tag, type TagIdentifiable } from "../model";
import { getTable, QUERY_KEYS } from "./shared";

export function buildRecord(params: Tag): TagIdentifiable {
	const base = createTag({
		...params,
		label: params.label.trim(),
	});

	return {
		...base,
		...generateDatabaseProperties({ table: "tags" }),
	};
}

/**
 * Create a tag and save it.
 * Throws if a tag with the same label (case-insensitive) already exists.
 */
export async function createAndSaveRecord(
	params: Partial<TagIdentifiable>,
): Promise<TagIdentifiable> {
	const existing = await getTable()
		.filter(
			(t) =>
				t.id === params.id || new RegExp(t.label, "i").test(params.label ?? ""),
		)
		.first();

	const record = buildRecord(params as Tag);

	if (existing) {
		return existing;
	}

	await database.transaction("rw", getTable(), async () => {
		await getTable().add(record);
	});
	return record;
}

type UseMutationCreateRecordProps = Parameters<
	typeof useMutation<TagIdentifiable, Error, Tag, unknown>
>[0];

export function useMutationCreateRecord({
	onSuccess,
	...props
}: Omit<UseMutationCreateRecordProps, "mutationFn" | "onSuccess"> & {
	onSuccess?: (response: TagIdentifiable, queryClient: QueryClient) => void;
} = {}) {
	const queryClient = useQueryClient();

	return useMutation({
		...props,
		mutationFn: createAndSaveRecord,
		onSuccess: (response: any) => {
			queryClient.invalidateQueries({ queryKey: QUERY_KEYS.records({}) });

			addToast({
				title: `Tag '${response.label}' created successfully`,
			});

			onSuccess?.(response, queryClient);
		},
	} as any);
}
